import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
} from '@/design/components';
import { supabase } from '@/lib/supabase';
import { useMe } from '@/auth/useMe';
import { usePerm } from '@/access/usePerm';
import { useEmployeeOptions } from '@/features/employees/api';
import { formatDate, WEEKDAYS } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { SpecialDaysPanel } from './SpecialDaysPanel';
import type { ClosureDefault, SpecialDay } from './specialDaysApi';

/** 0 = ראשון, כמו extract(dow) בפוסטגרס ו getDay בדפדפן. */

type ClosureRow = {
  id: string;
  branch_id: string | null;
  date: string | null;
  weekday: number | null;
  shift_type_id: string | null;
  is_closed: boolean;
  label: string;
  note: string | null;
  branches: { name: string } | null;
  shift_types: { name: string } | null;
};

const KEY = ['schedule-closures'] as const;
const FIELD = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

function useClosures() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<ClosureRow[]> => {
      const { data, error } = await supabase
        .from('schedule_closures')
        .select(
          'id, branch_id, date, weekday, shift_type_id, is_closed, label, note, branches ( name ), shift_types ( name )',
        )
        .eq('is_active', true)
        .order('weekday', { ascending: true, nullsFirst: false })
        .order('date', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ClosureRow[];
    },
  });
}

function useShiftTypes() {
  return useQuery({
    queryKey: ['shift-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shift_types')
        // start_time כדי לדעת מי המשמרת האחרונה ביום, ל"סגירת צהריים"
        .select('id, name, start_time')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * ימים סגורים וכללים קבועים (השלמה ל PRD 5.4).
 *
 * כאן יושבים הכללים שחוזרים כל שבוע, כמו "שבת סגורה" או "שישי רק בוקר".
 * יום חד פעמי כמו חג או יום בחירות נוח יותר לסמן ישירות מלוח הסידור,
 * ושניהם מופיעים באותה רשימה.
 */
export function ClosuresPage() {
  const canManage = usePerm('schedule.manage');
  const { me } = useMe();
  const queryClient = useQueryClient();
  const closures = useClosures();
  const { data: options } = useEmployeeOptions();
  const { data: shiftTypes } = useShiftTypes();

  /**
   * המשמרת שנסגרת ב"סגירת צהריים".
   *
   * האחרונה ביום ולא "ערב" לפי שם: שמות המשמרות הם נתון ונערכים
   * בהגדרות, ורשת אחת קוראת לה ערב ואחרת צהריים (כלל 1).
   */
  const afternoonShiftId = (shiftTypes ?? [])
    .slice()
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .at(-1)?.id;

  /**
   * הטופס פתוח, ועל מה. null = סגור, 'new' = הוספה, אחרת מזהה
   * הכלל שנערך. קודם אפשר היה רק להוסיף, ושינוי של פסיק בניסוח
   * חייב מחיקה ובנייה מחדש.
   */
  const [editing, setEditing] = useState<'new' | string | null>(null);
  /** 'weekly' = חוזר כל שבוע, 'once' = תאריך מסוים */
  const [kind, setKind] = useState<'weekly' | 'once'>('weekly');
  const [branchId, setBranchId] = useState('');
  const [weekday, setWeekday] = useState('6');
  const [date, setDate] = useState('');
  const [shiftTypeId, setShiftTypeId] = useState('');
  const [isClosed, setIsClosed] = useState(true);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  function openNew() {
    setError(null);
    setKind('weekly');
    setBranchId('');
    setWeekday('6');
    setDate('');
    setShiftTypeId('');
    setIsClosed(true);
    setLabel('');
    setNote('');
    setEditing('new');
  }

  /**
   * יום מיוחד הופך לטופס מלא מראש, ולא לכלל גמור.
   *
   * ההצעה היא ברירת מחדל: רשת יכולה להחליט לפתוח בערב חג, או
   * לסגור רק סניף אחד. לכן נפתח כאן הטופס הרגיל, עם הכול כבר
   * ממולא, והלחיצה האחרונה היא של המנהל.
   */
  function openFromSpecialDay(day: SpecialDay, suggestion: ClosureDefault) {
    setError(null);
    setKind('once');
    setDate(day.date);
    setBranchId('');
    setWeekday('6');
    setLabel(day.name);
    setNote('');
    setIsClosed(suggestion !== 'none');
    setShiftTypeId(
      suggestion === 'afternoon' ? (afternoonShiftId ?? '') : '',
    );
    setEditing('new');
  }

  function openEdit(row: ClosureRow) {
    setError(null);
    setKind(row.weekday !== null ? 'weekly' : 'once');
    setBranchId(row.branch_id ?? '');
    setWeekday(String(row.weekday ?? 6));
    setDate(row.date ?? '');
    setShiftTypeId(row.shift_type_id ?? '');
    setIsClosed(row.is_closed);
    setLabel(row.label);
    setNote(row.note ?? '');
    setEditing(row.id);
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });

  const save = useMutation({
    mutationFn: async () => {
      const values = {
        branch_id: branchId || null,
        // בדיוק אחד מהשניים, כמו האילוץ בבסיס הנתונים
        weekday: kind === 'weekly' ? Number(weekday) : null,
        date: kind === 'once' ? date : null,
        shift_type_id: shiftTypeId || null,
        is_closed: isClosed,
        label: label.trim(),
        note: note.trim() || null,
      };

      const { error: failure } =
        editing === 'new'
          ? await supabase
              .from('schedule_closures')
              .insert({ ...values, created_by: me?.employeeId ?? null })
          : await supabase
              .from('schedule_closures')
              .update({ ...values, updated_at: new Date().toISOString() })
              .eq('id', editing ?? '');
      if (failure) throw failure;
    },
    onSuccess: () => {
      void refresh();
      setEditing(null);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: failure } = await supabase.from('schedule_closures').delete().eq('id', id);
      if (failure) throw failure;
    },
    onSuccess: refresh,
    onError: (caught) => setError(errorMessage(caught)),
  });

  if (!canManage) return <Navigate to="/admin" replace />;

  const rows = closures.data ?? [];
  const recurring = rows.filter((row) => row.weekday !== null);
  const oneOff = rows.filter((row) => row.date !== null);

  return (
    <AdminPage
      title="ימים סגורים"
      subtitle="מתי לא עובדים, ומה מוצג לעובדים על היום"
      backTo="/admin/schedule"
      actions={
        editing === null ? (
          <Button variant="primary" onClick={openNew}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            הוספת יום סגור
          </Button>
        ) : undefined
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {editing !== null && (
        <Panel title={editing === 'new' ? 'יום סגור או הערה' : 'עריכת הכלל'}>
          <form
            className="grid gap-3 lg:max-w-4xl lg:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              save.mutate();
            }}
          >
            <fieldset className="grid gap-2 border-0 p-0 lg:col-span-2">
              <legend className="text-label font-medium">מתי</legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex min-h-11 items-center gap-2 text-body">
                  <input
                    type="radio"
                    name="closure-kind"
                    checked={kind === 'weekly'}
                    onChange={() => setKind('weekly')}
                    className="size-5"
                  />
                  חוזר כל שבוע
                </label>
                <label className="flex min-h-11 items-center gap-2 text-body">
                  <input
                    type="radio"
                    name="closure-kind"
                    checked={kind === 'once'}
                    onChange={() => setKind('once')}
                    className="size-5"
                  />
                  תאריך מסוים
                </label>
              </div>
              <p className="m-0 text-body-sm text-muted">
                שבת סגורה היא כלל שחוזר. חג או יום בחירות הם תאריך מסוים, ולא ישפיעו על אותו יום
                בשבועות אחרים.
              </p>
            </fieldset>

            <div className="grid content-start gap-1">
              <label htmlFor="closure-branch" className="text-label font-medium">
                סניף
              </label>
              <select
                id="closure-branch"
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                className={FIELD}
              >
                <option value="">כל הסניפים</option>
                {(options?.branches ?? []).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <p className="m-0 text-body-sm text-muted">
                כלל של סניף מסוים גובר על כלל שחל על כולם.
              </p>
            </div>

            {kind === 'weekly' ? (
              <div className="grid content-start gap-1">
                <label htmlFor="closure-weekday" className="text-label font-medium">
                  יום בשבוע *
                </label>
                <select
                  id="closure-weekday"
                  value={weekday}
                  onChange={(event) => setWeekday(event.target.value)}
                  className={FIELD}
                >
                  {WEEKDAYS.map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid content-start gap-1">
                <label htmlFor="closure-date" className="text-label font-medium">
                  תאריך *
                </label>
                <input
                  id="closure-date"
                  type="date"
                  dir="ltr"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  className={FIELD}
                />
                <p className="m-0 text-body-sm text-muted">
                  {date ? weekdayLabel(date) : 'חל על היום הזה בלבד.'}
                </p>
              </div>
            )}

            <div className="grid content-start gap-1">
              <label htmlFor="closure-shift" className="text-label font-medium">
                משמרת
              </label>
              <select
                id="closure-shift"
                value={shiftTypeId}
                onChange={(event) => setShiftTypeId(event.target.value)}
                className={FIELD}
              >
                <option value="">כל היום</option>
                {(shiftTypes ?? []).map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {shift.name} בלבד
                  </option>
                ))}
              </select>
              <p className="m-0 text-body-sm text-muted">
                לסגירת ערב שישי: לבחור כאן ערב, ואז הבוקר נשאר פתוח.
              </p>
            </div>

            <div className="grid content-start gap-1">
              <label htmlFor="closure-text" className="text-label font-medium">
                מה לכתוב *
              </label>
              <input
                id="closure-text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="שבת"
                className={FIELD}
              />
            </div>

            <div className="grid content-start gap-1 lg:col-span-2">
              <label htmlFor="closure-note" className="text-label font-medium">
                פירוט נוסף
              </label>
              <textarea
                id="closure-note"
                value={note}
                rows={3}
                onChange={(event) => setNote(event.target.value)}
                placeholder="למשל: הסניף סגור לספירת מלאי, נפתח ביום ראשון כרגיל"
                className="rounded-sm border border-muted bg-surface p-3 text-body text-ink placeholder:text-muted"
              />
              <p className="m-0 text-body-sm text-muted">
                מוצג לעובדים מתחת לכותרת, בסידור שלהם.
              </p>
            </div>

            <label className="flex min-h-11 items-center gap-2 text-body lg:col-span-2">
              <input
                type="checkbox"
                checked={isClosed}
                onChange={(event) => setIsClosed(event.target.checked)}
                className="size-5"
              />
              סגור, אי אפשר לשבץ. בלי סימון זו רק הערה שמוצגת על היום.
            </label>

            <div className="flex gap-2 lg:col-span-2">
              <Button
                type="submit"
                variant="primary"
                disabled={
                  save.isPending || label.trim() === '' || (kind === 'once' && date === '')
                }
              >
                {editing === 'new' ? 'הוספה' : 'שמירת השינויים'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(null)}>
                ביטול
              </Button>
            </div>
          </form>
        </Panel>
      )}

      {editing === null && <SpecialDaysPanel onCreate={openFromSpecialDay} />}

      {closures.isPending && <Skeleton className="h-24 w-full" />}
      {closures.isError && <ErrorState onRetry={() => void closures.refetch()} />}

      {closures.data && rows.length === 0 && (
        <EmptyState
          title="אין כללים"
          description="בלי כללים הסידור נבנה כאילו עובדים בכל יום ובכל משמרת."
        />
      )}

      {recurring.length > 0 && (
        <Panel title="כל שבוע" bodyClassName="p-0">
          <ClosureTable
            rows={recurring}
            onEdit={openEdit}
            onRemove={(id) => remove.mutate(id)}
          />
        </Panel>
      )}

      {oneOff.length > 0 && (
        <Panel title="תאריכים מסוימים" bodyClassName="p-0">
          <ClosureTable rows={oneOff} onEdit={openEdit} onRemove={(id) => remove.mutate(id)} />
        </Panel>
      )}
    </AdminPage>
  );
}

/** משוב מיידי על התאריך שנבחר, כדי שלא ייסגר יום בטעות. */
function weekdayLabel(date: string): string {
  const day = new Date(`${date}T00:00:00`).getDay();
  return `יום ${WEEKDAYS[day]}, פעם אחת בלבד.`;
}

function ClosureTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: ClosureRow[];
  onEdit: (row: ClosureRow) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>מתי</Th>
          <Th>סניף</Th>
          <Th>משמרת</Th>
          <Th>מה מוצג</Th>
          <Th>מצב</Th>
          <Th className="text-end">פעולות</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td className="font-medium whitespace-nowrap">
              {row.weekday !== null ? `כל ${WEEKDAYS[row.weekday]}` : formatDate(row.date ?? '')}
            </Td>
            <Td className="text-muted">{row.branches?.name ?? 'כל הסניפים'}</Td>
            <Td className="text-muted">{row.shift_types?.name ?? 'כל היום'}</Td>
            <Td>
              <span className="grid gap-0.5">
                <span>{row.label}</span>
                {row.note && <span className="text-label text-muted">{row.note}</span>}
              </span>
            </Td>
            <Td>
              {row.is_closed ? (
                <StatusBadge tone="bad">סגור</StatusBadge>
              ) : (
                <StatusBadge tone="warn">הערה</StatusBadge>
              )}
            </Td>
            <Td className="text-end whitespace-nowrap">
              <button
                type="button"
                aria-label={`עריכת הכלל ${row.label}`}
                title="עריכה"
                onClick={() => onEdit(row)}
                className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink hover:bg-cream"
              >
                <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={`מחיקת הכלל ${row.label}`}
                title="מחיקה"
                onClick={() => onRemove(row.id)}
                className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink hover:bg-cream"
              >
                <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
